// app/data/counselorSuggestions.js

/* ------------------------------ Common reasons dropdown ------------------------------ */
export const needReasons = [
  // Core admissions & contextual support
  "Required counselor recommendation for freshman application",
  "Optional counselor statement requested by admissions",
  "Midyear report / counselor update requested",
  "Early Action/Early Decision supporting letter",
  "Waitlist — letter of continued interest support",
  "Deferral — context and update letter support",
  "Rolling admission — priority review context",

  // Scholarships & financial context
  "Scholarship consideration requires a counselor letter",
  "Competitive scholarship finalist interview support",
  "Need-based aid context (financial hardship / extenuating circumstances)",
  "Fee waiver justification / verification",
  "Merit scholarship reconsideration / appeal",
  "Special circumstances / professional judgment (FAFSA/CSS Profile update)",

  // Programs, colleges, and tracks
  "Honors/Scholars program application",
  "Living-Learning / Residential College application",
  "Co-op or experiential learning program application",
  "Global scholars / study-abroad focused program",
  "Bridge or summer enrichment program application",
  "Fly-in / diversity program application",
  "Research institute / lab placement interest",
  "Service-learning or civic leadership program application",
  "Demonstrated interest and institutional fit", // New: Highlights that the counselor can confirm student's interest

  // Major-specific or selective pathways
  "Competitive/impacted major (e.g., Computer Science, Engineering)",
  "Auditioned arts program (music/theatre/dance)",
  "Portfolio-based program (art/design/architecture)",
  "Pre-health interest — fit and preparation context",
  "Business/finance selective cohort application",
  "Education/teaching pathway endorsement",
  "Data science / AI pathway endorsement",
  "Interdisciplinary program application (e.g., STEM + Humanities)", // New: Reflects modern program types

  // Athletics
  "Athletic recruitment support",
  "NCAA eligibility / character reference",
  "Club-to-varsity transition context",

  // Student context & equity
  "Context for academic/personal circumstances",
  "Significant transcript anomalies (school moves, closures, grading changes)",
  "Limited AP/IB/advanced offerings — school profile context",
  "Test-optional application context",
  "English learner (EL) support context",
  "Disability/IEP/504 accommodations — support and advocacy context",
  "Health or attendance disruptions — recovery and resilience",
  "Disciplinary record explanation and growth",
  "Academic integrity incident — reflection and remediation",
  "Caregiving or work responsibilities impacting academics",
  "Post-pandemic academic/social adjustment context", // New: Acknowledges ongoing challenges
  "Socioeconomic or family context beyond FAFSA/CSS", // New: Broader explanation of home life

  // Pathway & background
  "First-generation and/or low-income student support",
  "International student context (school system & transition)",
  "Homeschool verification and academic context",
  "Foster care / McKinney-Vento (housing insecurity) support",
  "Military-affiliated / ROTC interest and character reference",
  "Adult/re-entry learner context",
  "Undocumented/DACA context and support",

  // Application type & timing
  "Transfer application support",
  "Reinstatement after leave of absence",
  "Gap year explanation and structured plan",
  "Readmission after academic dismissal",
  "Major change or internal transfer request",

  // Evidence & supplementation
  "Supplement to teacher recommendations",
  "Leadership and community impact reinforcement",
  "Dual enrollment / college coursework context",
  "Career & Technical Education (CTE) pathway endorsement",
  "Updated accomplishments since application submission",
  "New test scores / certifications context",
  "Confirmation of extenuating circumstances", // New: Broadens the scope beyond just financial
  "Verification of specific extracurricular role/impact", // New: Confirms a student's role in a group

  // Regional & policy
  "Regional admissions program / local residency priority",
  "Automatic admission policy verification (rank-based or GPA-based)",
  "State flagship / top-10% plan verification",

  // Decisions & logistics
  "Admission decision appeal with new information",
  "Housing/commute hardship documentation",
  "Visa/immigration timeline support for international student",
];

/* ------------------------------ Suggestion banks ------------------------------ */
export const achievementOptions = [
  // Academic distinction & coursework
  "IB Diploma / AP Capstone (Seminar + Research)",
  "AP Scholar with Distinction",
  "Dual Enrollment (college coursework with A/B grades)",
  "National Merit Semifinalist/Finalist",
  "Dean’s List or President’s Education Award",
  "Significant upward academic trajectory", // New: Highlights growth over time

  // Research, innovation & comps
  "Regeneron STS / ISEF Finalist",
  "Published Research / Preprint (co-author or first author)",
  "AIME Qualifier / USAMO Participant",
  "USACO Gold (or higher) / ACSL Finalist",
  "Science Olympiad Medalist (state/national)",
  "Hackathon Winner (team or solo)",
  "Patent / Provisional Patent Holder",
  "Noteworthy personal project with public impact", // New: Focus on projects outside of competitions

  // Leadership & impact
  "Student Body President / Class Officer",
  "Robotics Team Captain (FRC/VEX)",
  "Yearbook or Newspaper Editor-in-Chief",
  "Varsity Team Captain (athletics)",
  "Founder of Club / Nonprofit / Social Enterprise",
  "Community Service Project with 100+ documented hours & measurable impact",
  "Fundraising Lead (>$5K raised) for community cause",
  "Created a mentorship program for underclassmen", // New: Shows initiative and commitment to others

  // Career exposure
  "Internship (university lab / company) with deliverables",
  "Open-source Contributor (merged PRs / maintainer)",
  "App with 10K+ users or notable traction",
  "Sustained work experience or family responsibilities", // New: Validates a non-traditional "extracurricular"

  // Competitive orgs & arts
  "HOSA International Finalist",
  "DECA/FBLA State or Nationals Finalist",
  "Model UN Best Delegate / Outstanding Delegate",
  "All-State Musician (band/choir/orchestra)",
  "Scholastic Art & Writing Gold Key",
  "National Language Exam Gold Medal (NLE/DELE/HSK/JLPT)",

  // Selective programs
  "Governor’s School / highly selective summer institute alum",
  "Girls Who Code / Kode With Klossy alum",
  "Participant in a college fly-in or diversity program", // New: Shows demonstrated interest
];

export const hardSkillOptions = [
  // Programming & data
  "Python (NumPy, Pandas, scikit-learn)",
  "Java",
  "C++",
  "JavaScript",
  "TypeScript",
  "R (tidyverse)",
  "SQL (PostgreSQL/MySQL)",
  "NoSQL (MongoDB/Firestore)",
  "APIs & REST/JSON",
  "Data Analysis & Visualization (Matplotlib/Plotly/Tableau)",
  "Statistics & Probability (A/B testing, inference)",
  "Machine Learning Fundamentals",
  "Jupyter/Colab Notebooks",

  // Web & apps
  "React / Next.js",
  "Node.js / Express",
  "HTML/CSS (responsive, accessibility)",
  "UI/UX & Figma basics",

  // Systems & tooling
  "Linux / CLI",
  "Git / GitHub (branches, PRs)",
  "CI/CD (GitHub Actions)",
  "Docker / Containers",
  "Cloud Fundamentals (AWS/GCP/Azure)",
  "Cybersecurity Basics",
  "Networking Fundamentals",

  // Engineering & science
  "CAD (SolidWorks/Fusion 360)",
  "3D Printing / Additive Manufacturing",
  "CNC / CAM basics",
  "Electronics & Microcontrollers (Arduino/Raspberry Pi)",
  "Lab Techniques & Safety (pipetting, gels, titration)",
  "Scientific Writing & LaTeX",
  "Advanced Excel / Google Sheets (pivot tables, lookups)",
  "GIS/geospatial analysis", // New: A growing field of interest
];

export const softSkillOptions = [
  "Leadership & Initiative",
  "Time Management & Organization",
  "Collaboration & Teamwork",
  "Clear Written & Oral Communication",
  "Critical Thinking & Problem Decomposition",
  "Independent Research & Self-Directed Learning",
  "Adaptability & Resilience",
  "Creativity & Innovation",
  "Ethics & Academic Integrity",
  "Growth Mindset & Reflective Practice",
  "Attention to Detail",
  "Project Planning & Execution",
  "Mentorship & Peer Coaching",
  "Cross-Cultural Communication",
  "Conflict Resolution & Negotiation",
  "Resourcefulness & Grit",
  "Public Speaking / Technical Presentations",
  "Data Literacy & Evidence-based Reasoning",
  "Collaboration in Diverse Teams",
  "Customer/Community Empathy",
  "Accountability & Follow-through",
  "Networking & Professional Etiquette",
  "Facilitation & Meeting Leadership",
  "Civic Engagement & Service Orientation",
  "Interpersonal skills & emotional intelligence", // New: A key trait for campus community fit
];

export const reasonSuggestions = [
  "Academic preparation aligns with program rigor",
  "Leadership record matches the college’s values",
  "Sustained community impact and service",
  "Evidence of intellectual curiosity and research potential",
  "Demonstrated resilience and upward trajectory",
  "Strong fit for collaborative, project-based learning",
  "Clear motivation and purpose for chosen major",
  "Interdisciplinary strengths that bridge multiple fields",
  "Meaningful real-world experience (work, caregiving, entrepreneurship)",
  "Potential to contribute to research labs or institutes",
  "Readiness for honors / capstone work",
  "Alignment with experiential education / co-op model",
  "Unique perspective that enriches campus diversity",
  "Track record of self-advocacy and initiative",
  "Clear professional goals with a realistic plan",
  "Commitment to public service / civic engagement",
  "Ability to lead and mentor peers on campus",
  "Values alignment with the institution’s mission and community",
  "Demonstrated interest and institutional fit", // New: Shows they've done their research on the school
  "Ability to work effectively in a team environment", // New: A core skill for college life
  "Contribution to the overall health and well-being of the school community", // New: Focuses on positive character
];